// GA4 client-side page_view helper for the SPA.
//
// gtag.js only auto-sends a page_view on the initial document load. Because the
// marketing app client-side redirects `/` to `/<locale>/` and then navigates via
// React Router, relying on that single auto hit under-reports pageviews and
// attributes everything to the pre-redirect URL.
//
// `send_page_view: false` is set in the gtag config (index.html /
// index-marketing.html), so this helper owns every page_view, including the first.

import { isGaDebugMode, isProdHost } from "@/lib/analyticsHost";
import { canSendClientAnalytics } from "@/lib/europeVisitor";

const GA4_MEASUREMENT_ID = "G-M09E85VL9J";

// Last path that actually reached gtag. ConsentScriptsGate sends as soon as
// Europe grants analytics; useGAPageView may still have a 350ms timer from
// the same landing URL. Deduping here drops the overlap without blocking a
// later navigation, or a grant that arrives after a denied first tick.
let lastSentPath: string | null = null;

/**
 * Send a GA4 `page_view` for the current URL.
 * No-ops when gtag is unavailable (non-prod hosts, ad-blockers, captive portals).
 */
export function sendPageView(overrides: Record<string, unknown> = {}): void {
  // Production-only. Preview/localhost must never reach the live property, even
  // if a third-party tag (e.g. HubSpot's Google Ads tag) pulled in gtag.js.
  if (!isProdHost()) return;
  // The gtag stub in index.html is always a function. Without this check,
  // EU page_views would queue in dataLayer and flush when gtag.js later loads.
  if (!canSendClientAnalytics()) return;
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;

  const page_path = `${window.location.pathname}${window.location.search}`;
  if (lastSentPath === page_path) return;

  try {
    window.gtag("event", "page_view", {
      send_to: GA4_MEASUREMENT_ID,
      page_path,
      page_location: window.location.href,
      page_title: document.title,
      ...(isGaDebugMode() ? { debug_mode: 1 } : {}),
      ...overrides,
    });
    lastSentPath = page_path;
  } catch {
    /* never break navigation for analytics */
  }
}
